import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Target, Trophy, ArrowRight } from "lucide-react";

const API = '/api';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [activeEvents, setActiveEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const t = Date.now();
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/stats?t=${t}`),
        axios.get(`${API}/events?t=${t}`),
      ]);
      setStats(statsRes.data);
      setActiveEvents(eventsRes.data.filter(e => e.status === "active").slice(0, 3));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-2">
          Welcome to the NMLRA Trap Shooting Tracker
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover" data-testid="stat-events">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Active Events</p>
                <p className="font-mono text-3xl font-bold text-[#1B3B6F] mt-1">
                  {stats?.active_events || 0}
                </p>
              </div>
              <div className="p-3 bg-[#1B3B6F]/10 rounded-lg">
                <Calendar className="text-[#1B3B6F]" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-shooters">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Shooters</p>
                <p className="font-mono text-3xl font-bold text-[#1B3B6F] mt-1">
                  {stats?.total_shooters || 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="text-green-700" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-rounds">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Rounds Shot</p>
                <p className="font-mono text-3xl font-bold text-[#1B3B6F] mt-1">
                  {stats?.total_rounds || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Target className="text-orange-500" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-paid">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Paid Entries</p>
                <p className="font-mono text-3xl font-bold text-[#1B3B6F] mt-1">
                  {stats?.total_paid_entries || 0}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <Trophy className="text-amber-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Active Events */}
        <Card data-testid="active-events-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-xl">Active Events</CardTitle>
          </CardHeader>
          <CardContent>
            {activeEvents.length === 0 ? (
              <p className="text-slate-500 py-4">No active events. Create one to get started!</p>
            ) : (
              <div className="space-y-3">
                {activeEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    data-testid={`event-item-${event.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-800">{event.name}</p>
                      <p className="text-sm text-slate-500 font-mono">
                        ${event.entry_fee} entry
                      </p>
                    </div>
                    <Link to={`/leaderboard/${event.id}`}>
                      <Button variant="ghost" size="sm" data-testid={`view-leaderboard-${event.id}`}>
                        View <ArrowRight size={16} className="ml-1" />
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
            <Link to="/events" className="block mt-4">
              <Button 
                className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                data-testid="manage-events-btn"
              >
                Manage Events
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Quick Start */}
        <Card data-testid="quick-actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/score-entry" className="block">
              <Button
                variant="outline"
                className="w-full justify-start h-14 text-left border-2 hover:bg-green-50 hover:border-green-500"
                data-testid="quick-score-entry"
              >
                <Target className="mr-3 text-green-700" size={24} />
                <div>
                  <p className="font-medium">Enter Scores</p>
                  <p className="text-xs text-slate-500">Record a new round</p>
                </div>
              </Button>
            </Link>

            <Link to="/shooters" className="block">
              <Button
                variant="outline"
                className="w-full justify-start h-14 text-left border-2 hover:bg-blue-50 hover:border-blue-500"
                data-testid="quick-add-shooter"
              >
                <Users className="mr-3 text-[#1B3B6F]" size={24} />
                <div>
                  <p className="font-medium">Add Shooter</p>
                  <p className="text-xs text-slate-500">Register a new participant</p>
                </div>
              </Button>
            </Link>

            <Link to="/payments" className="block">
              <Button
                variant="outline"
                className="w-full justify-start h-14 text-left border-2 hover:bg-amber-50 hover:border-amber-500"
                data-testid="quick-payments"
              >
                <Trophy className="mr-3 text-amber-600" size={24} />
                <div>
                  <p className="font-medium">Track Payments</p>
                  <p className="text-xs text-slate-500">Manage entry fees</p>
                </div>
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
